import React from 'react';
import { FieldProps } from 'formik';
import SurveyItem from '../../util/SurveyItem';
import SurveyField from '../SurveyField';
import withCheckForEnablement, {
  WithCheckForEnablementInjectedProps,
} from '../hoc/withCheckForEnablement';
import SurveyItemContainer from '../SurveyItemContainer';
import TypedTextField, {
  SupportedTypes,
} from '../InputControls/TypedTextField/TypedTextField';
import withValidation, {
  WithValidationInjectedProps,
} from '../hoc/withValidation';
import { withIsReadOnly, WithIsReadOnlyInjectedProps } from '../Contexts';
import getSurveyItemTestId from '../../util/getSurveyItemTestId';
import { getTestProps } from '../../../../util/testProps';
import {
  WithStyles,
  createStyles,
  withStyles,
} from '@lifeomic/chroma-react/styles';
import clsx from 'clsx';
import { inputCarriesFieldWidth } from '../../util/fieldWidths';
import { surveyItemHeaderLabel } from '../CalculatedValueIndicator';

const styles = () =>
  createStyles({
    root: {
      width: '100%',
      maxWidth: 500,
    },
    // Plain-textarea notes fields get a roomier cap than single-line inputs;
    // the opt-in field-width extension (SurveyItemContainer) still binds first.
    plainTextAreaRoot: {
      maxWidth: 900,
    },
  });

export const testIds = {
  textField: (linkId: string) => getSurveyItemTestId(linkId),
};

export interface FieldComponentOwnProps {
  validate: (value: any) => void;
  responseAnswerType: string;
  surveyItem: SurveyItem;
  isReadOnly: boolean;
  error: string;
  shouldShowError: boolean;
  className?: string;
  overrideUnitLabel?: string;
}

export type FieldComponentProps = FieldComponentOwnProps & FieldProps<any>;

class FieldComponent extends React.PureComponent<FieldComponentProps> {
  handleChange = (event: any) => {
    const { form, field } = this.props;
    const fieldValue = event.target.value;

    form.setFieldValue(field.name, fieldValue);
  };

  render() {
    const {
      field: { value, onChange, ...inputProps },
      form,
      surveyItem,
      isReadOnly,
      error,
      shouldShowError,
      ...toPass
    } = this.props;

    return (
      <TypedTextField
        hasError={shouldShowError}
        onChange={this.handleChange}
        fhirType={surveyItem.type as SupportedTypes}
        label={
          surveyItem.hasHiddenLabel
            ? undefined
            : // Chroma types label as string; ReactNode works at runtime so the
              // calculator icon can sit after the title.
              (surveyItemHeaderLabel(surveyItem, surveyItem.label) as
                | string
                | undefined)
        }
        aria-label={surveyItem.hasHiddenLabel ? surveyItem.label : undefined}
        value={value}
        disabled={isReadOnly}
        errorMessage={shouldShowError ? error : undefined}
        fullWidth
        mappingInitialValue={surveyItem.mappingInitialValue}
        enforcedPrecision={surveyItem.valuePrecision}
        plainTextArea={surveyItem.isPlainTextArea}
        fieldWidth={
          inputCarriesFieldWidth(surveyItem.type) ? surveyItem.fieldWidth : null
        }
        linkId={surveyItem.link}
        {...getTestProps(testIds.textField(inputProps.name))}
        {...inputProps}
        {...toPass}
      />
    );
  }
}

export interface TextOwnProps {
  surveyItem: SurveyItem;
}

export type TextProps = TextOwnProps &
  WithIsReadOnlyInjectedProps &
  WithCheckForEnablementInjectedProps &
  WithValidationInjectedProps &
  WithStyles<typeof styles>;

class Text extends React.PureComponent<TextProps> {
  render() {
    const {
      surveyItem,
      isEnabled,
      shouldShowError,
      error,
      validate,
      isReadOnly,
      classes,
    } = this.props;

    if (!isEnabled) return null;

    return (
      <SurveyItemContainer surveyItem={surveyItem}>
        <SurveyField
          className={clsx(
            classes.root,
            surveyItem.isPlainTextArea && classes.plainTextAreaRoot,
          )}
          validate={validate}
          initialValue={surveyItem.initialValue}
          name={surveyItem.link}
          component={FieldComponent}
          surveyItem={surveyItem}
          isReadOnly={isReadOnly || surveyItem.hasCalculatedValue}
          responseAnswerType={surveyItem.responseValueType}
          error={error}
          shouldShowError={shouldShowError}
          overrideUnitLabel={surveyItem.unitLabel}
        />
      </SurveyItemContainer>
    );
  }
}

export default withIsReadOnly(
  withCheckForEnablement(withValidation(withStyles(styles)(Text))),
);
